import React from 'react';
import styled from 'styled-components';
import { palette } from './../styles/color';
import { NavLink, useLocation, useNavigate } from 'react-router';
import { Typography } from 'design-component';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'store/store';
import { userInfoSelector } from 'store/slices/userInfo';
import { IconArrowTopRight } from 'assets/icon';

const Sidebar = () => {
  const { t } = useTranslation();
  // const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const { isLogged } = useAppSelector(userInfoSelector);

  const ROUTES = [
    {
      path: '/playground',
      subMenu: [
        {
          label: 'PLAYGROUND',
          children: [
            {
              label: 'Doc Extract',
              path: '/doc-extract'
            }
          ]
        }
      ]
    },
    {
      path: '/_api',
      subMenu: [
        {
          label: 'API',
          children: [
            {
              label: 'API Reference',
              path: '/reference'
            },
            {
              label: 'API Keys',
              path: '/keys'
            },
            ...(isLogged
              ? [
                  {
                    label: 'API Usage',
                    path: '/usage'
                  }
                ]
              : [])
          ]
        }
      ]
    },
    {
      path: '/documentation',
      subMenu: [
        {
          label: `${t('gettingStarted')}`,
          children: [
            {
              label: `${t('overview')}`,
              path: '?docType=overview'
            },
            {
              label: `${t('quickStart')}`,
              path: '?docType=quickstart'
            }
          ]
        },
        {
          label: `${t('learnAbout')}`,
          children: [
            {
              label: `${t('useCases')}`,
              path: '?docType=use_cases'
            },

            {
              label: `${t('pricing')}`,
              path: '?docType=pricing'
            }
          ]
        },
        {
          label: `${t('capabilities')}`,
          children: [
            {
              label: `${t('docToJson')}`,
              path: '?docType=doc_extract'
            }
          ]
        },
        {
          label: `${t('resources')}`,
          children: [
            {
              label: `${t('rateLimits')}`,
              path: '?docType=limits'
            },
            {
              label: `${t('changelog')}`,
              path: '?docType=change_log'
            },
            {
              label: `${t('termsAndPolicy')}`,
              path: '?docType=terms_and_policies'
            },
            {
              label: `EULA`,
              path: '?docType=EULA'
            },
            {
              label: `Agent Skill`,
              path: '?docType=agent_skill'
            },
            {
              label: `${t('privacyPolicy')}`,
              href: 'https://www.polarisoffice.com/ko/privacy'
            },
            {
              label: `Cookbook`,
              href: 'https://github.com/PolarisOffice/PolarisAIDataInsight/tree/main/langchain-polaris-ai-datainsight/cookbook'
            },
            {
              label: `Doc Extract MCP`,
              href: 'https://github.com/PolarisOffice/PolarisAIDataInsight/tree/main/mcp-polaris-ai-datainsight'
            },
            {
              label: `Doc Extract on LangChain`,
              href: 'https://github.com/PolarisOffice/PolarisAIDataInsight/tree/main/langchain-polaris-ai-datainsight'
            }
          ]
        }
      ]
    }
  ];

  const currentRoute = ROUTES.find((r) => currentPath.startsWith(r.path));

  // // 현재 경로를 기반으로 초기 리다이렉션 처리
  // React.useEffect(() => {
  //   if (!currentRoute) return;

  //   // 현재 경로가 루트 경로인지 확인
  //   const isAtRoot = currentPath === currentRoute.path;

  //   if (isAtRoot) {
  //     const firstSubPage = currentRoute.subMenu?.[0]?.children?.[0];

  //     if (firstSubPage) {
  //       const redirectPath = currentRoute.path + firstSubPage.path;
  //       navigate(redirectPath, { replace: true });
  //     }
  //   }
  // }, [currentRoute, currentPath, navigate]);

  return (
    <Wrapper>
      <nav>
        {currentRoute && (
          <MenuList>
            {currentRoute.subMenu.map((item) => (
              <li key={item.label}>
                <Typography
                  component="p"
                  weight={700}
                  size="12px"
                  height="14px"
                  color={palette.gray[400]}
                  style={{
                    padding: '0 0 8px 10px'
                  }}>
                  {t(item.label)}
                </Typography>
                <ul>
                  {item.children.map((child) => {
                    const linkPath = currentRoute.path + child.path;

                    return (
                      <li key={child.label} style={{ marginBottom: '4px' }}>
                        {child.href ? (
                          <a href={child.href} target="_blank" rel="noreferrer">
                            <NavItemText component="div" weight={400} size="14px" height="17px">
                              <div>{t(child.label)}</div>
                              <IconArrowTopRight />
                            </NavItemText>
                          </a>
                        ) : (
                          <NavLink
                            to={linkPath}
                            style={() => {
                              const isExactMatch: boolean =
                                location.pathname + location.search === linkPath; // NOTE: docType가 포함된 정확한 경로 비교하기

                              return {
                                background: isExactMatch ? palette.gray[200] : undefined,
                                borderRadius: isExactMatch ? '10px' : undefined
                              };
                            }}>
                            <NavItemText component="div" weight={400} size="14px" height="17px">
                              {t(child.label)}
                            </NavItemText>
                          </NavLink>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))}
          </MenuList>
        )}
      </nav>
    </Wrapper>
  );
};

export default Sidebar;

const Wrapper = styled.div`
  min-width: 240px;
  height: 100%;
  padding: 20px;
  overflow-y: auto;

  border-right: 1px solid ${palette.gray[200]};
  background: ${palette.gray[100]};
`;

const MenuList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const NavItemText = styled(Typography)`
  padding: 13.5px 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-start;
`;
